// TotalShopCommissionSplitReport.jsx

import React, { useEffect, useState, useRef } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  Backdrop,
  Alert,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { DataGrid } from '@mui/x-data-grid';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { format } from 'date-fns';
import {jwtDecode} from 'jwt-decode';
import BackButton from '../../components/BackButton';
import { maskVpa } from '../../utils/maskVpa';
// [CASH-MODE] UPI / Cash filter + indication
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeChip from '../../components/PaymentModeChip';
import PaymentModeTotals from '../../components/PaymentModeTotals';
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeParam, paymentModeLabel, totalsByModeFromRows } from '../../utils/paymentMode';

const TotalShopCommissionSplitReport = () => {
  const { shopId } = useParams();
  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 30))
  );
  const [endDate, setEndDate] = useState(new Date());
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [grandTotals, setGrandTotals] = useState({
    totalAmount: 0,
    totalIncome: 0,
    gstAmount: 0,
    customerAmount: 0,
    customerWelfareAmount: 0,
    headOfficeAmount: 0,
    stateAdminAmount: 0,
    msbAmount: 0,
    averageCommissionPercentage: 0
  });
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [loading, setLoading] = useState(false);
  const [userLevel, setUserLevel] = useState(null);
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]
  const [apiTotalsByMode, setApiTotalsByMode] = useState(null); // [CASH-MODE]

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserLevel(decoded.level);
      } catch (err) {}
    }
  }, []);

  const componentRef = useRef();

  // Get userId from JWT token
  const token = sessionStorage.getItem('token');
  let userId = '';
  if (token) {
    try {
      const decodedToken = jwtDecode(token);
      userId = decodedToken.userId;
    } catch (error) {
      console.error('Error decoding token:', error);
    }
  }

  useEffect(() => {
    fetchSplitupData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, paymentMode]); // [CASH-MODE] paymentMode

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const validateDates = () => {
    if (!startDate || !endDate) {
      setDialogMessage('Please select both start date and end date.');
      setOpenDialog(true);
      return false;
    }
    const diffInDays = Math.ceil(
      (endDate - startDate) / (1000 * 60 * 60 * 24)
    );
    if (startDate > endDate) {
      setDialogMessage('Start date must be on or before the end date.');
      setOpenDialog(true);
      return false;
    }
    if (diffInDays > 31) {
      setDialogMessage('Date range cannot be more than 31 days.');
      setOpenDialog(true);
      return false;
    }
    return true;
  };

  const fetchSplitupData = async () => {
    if (!validateDates()) {
      return;
    }
    setLoading(true);
    try {
      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(endDate, 'yyyy-MM-dd');

      const response = await axios.post(
        `${API_BASE_URL}/shopReport/totalShopCommissionSplitups`,
        {
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          userId,
          shopId,
          paymentMode: paymentModeParam(paymentMode), // [CASH-MODE] undefined for ALL
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200) {
        const data = response.data;
        setTransactions(data.transactions || []);
        setGrandTotals(data.grandTotals || {
          totalAmount: 0,
          totalIncome: 0,
          gstAmount: 0,
          customerAmount: 0,
          customerWelfareAmount: 0,
          headOfficeAmount: 0,
          stateAdminAmount: 0,
          msbAmount: 0,
          averageCommissionPercentage: 0
        });
        setTotalTransactions(data.totalTransactions || 0);
        setApiTotalsByMode(data.totalsByMode || (data.grandTotals && data.grandTotals.totalsByMode) || null); // [CASH-MODE]
      } else {
        console.error('Failed to fetch data');
        setTransactions([]);
        setGrandTotals({
          totalAmount: 0,
          totalIncome: 0,
          gstAmount: 0,
          customerAmount: 0,
          customerWelfareAmount: 0,
          headOfficeAmount: 0,
          stateAdminAmount: 0,
          msbAmount: 0,
          averageCommissionPercentage: 0
        });
        setTotalTransactions(0);
        setApiTotalsByMode(null); // [CASH-MODE]
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response) {
        console.error('Backend error response:', error.response.data);
        setDialogMessage(
          error.response.data.message ||
            'An error occurred while fetching data.'
        );
        setOpenDialog(true);
      }
      setTransactions([]);
      setGrandTotals({
        totalAmount: 0,
        totalIncome: 0,
        gstAmount: 0,
        customerAmount: 0,
        customerWelfareAmount: 0,
        headOfficeAmount: 0,
        stateAdminAmount: 0,
        msbAmount: 0,
        averageCommissionPercentage: 0
      });
      setTotalTransactions(0);
      setApiTotalsByMode(null); // [CASH-MODE]
    }
    setLoading(false);
  };

  const handlePrint = async () => {
    if (transactions.length === 0) {
      setDialogMessage('No data to print.');
      setOpenDialog(true);
      return;
    }

    try {
      const input = componentRef.current;
      const canvas = await html2canvas(input, {
        scale: 2,
        logging: false,
        useCORS: true,
        windowWidth: 1400,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'pt', 'a4'); // Landscape for better table fit
      const imgWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`TotalShopCommissionSplitReport.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setDialogMessage('An error occurred while generating the PDF.');
      setOpenDialog(true);
    }
  };

  const columns = [
    { field: 'serialNumber', headerName: 'S.No.', width: 70 },
    { 
      field: 'date', 
      headerName: 'Date', 
      width: 110,
      renderCell: (params) => format(new Date(params.row.createdAt), 'dd-MM-yyyy')
    },
    { 
      field: 'vpaId', 
      headerName: 'VPA ID', 
      width: 180,
      renderCell: (params) => maskVpa(params.value, userLevel)
    },
    { 
      field: 'totalAmount', 
      headerName: 'Total Amount (₹)', 
      width: 130,
      renderCell: (params) => `₹${params.value.toFixed(2)}`
    },
    { 
      field: 'commissionPercentage', 
      headerName: 'Commission %', 
      width: 110,
      renderCell: (params) => `${params.value.toFixed(2)}%`
    },
    { 
      field: 'totalIncome', 
      headerName: 'Total Income (₹)', 
      width: 130,
      renderCell: (params) => `₹${params.value.toFixed(2)}`
    },
    { 
      field: 'gstAmount', 
      headerName: 'GST (₹)', 
      width: 110,
      renderCell: (params) => `₹${params.value.toFixed(2)}`
    },
    { 
      field: 'customerAmount', 
      headerName: 'Customer (₹)', 
      width: 120,
      renderCell: (params) => `₹${params.value.toFixed(2)}`
    },
    { 
      field: 'customerWelfareAmount', 
      headerName: 'Welfare (₹)', 
      width: 120,
      renderCell: (params) => `₹${params.value.toFixed(2)}`
    },
    { 
      field: 'headOfficeAmount', 
      headerName: 'HO (₹)', 
      width: 110,
      renderCell: (params) => `₹${params.value.toFixed(2)}`
    },
    { 
      field: 'stateAdminAmount', 
      headerName: 'State Admin (₹)', 
      width: 130,
      renderCell: (params) => `₹${params.value.toFixed(2)}`
    },
    { 
      field: 'msbAmount', 
      headerName: 'MSB (₹)', 
      width: 110,
      renderCell: (params) => `₹${params.value.toFixed(2)}`
    },
    // [CASH-MODE] UPI / Cash
    {
      field: 'paymentMode',
      headerName: 'Mode',
      width: 100,
      renderCell: (params) => <PaymentModeChip row={params.row} />
    }
  ];

  const rows = transactions.map((tx, index) => ({
    id: tx._id || index,
    serialNumber: index + 1,
    createdAt: tx.createdAt,
    vpaId: tx.vpaId,
    totalAmount: tx.totalAmount || 0,
    commissionPercentage: tx.commissionPercentage || 0,
    totalIncome: tx.totalIncome || 0,
    gstAmount: tx.gstAmount || 0,
    customerAmount: tx.customerAmount || 0,
    customerWelfareAmount: tx.customerWelfareAmount || 0,
    headOfficeAmount: tx.headOfficeAmount || 0,
    stateAdminAmount: tx.stateAdminAmount || 0,
    msbAmount: tx.msbAmount || 0,
    paymentMode: tx.paymentMode // [CASH-MODE]
  }));

  // [CASH-MODE] API totalsByMode when it carries totalAmount, else from rows
  const splitModeTotals = (apiTotalsByMode && apiTotalsByMode.UPI && apiTotalsByMode.UPI.totalAmount !== undefined)
    ? apiTotalsByMode
    : (() => {
        const t = totalsByModeFromRows(rows, (r) => r.totalAmount);
        return { UPI: { ...t.UPI, totalAmount: t.UPI.amount }, CASH: { ...t.CASH, totalAmount: t.CASH.amount } };
      })();

  const renderTableRows = () => {
    return rows.map((row) => (
      <tr key={row.id}>
        <td>{row.serialNumber}</td>
        <td>{format(new Date(row.createdAt), 'dd-MM-yyyy')}</td>
        <td>{maskVpa(row.vpaId, userLevel)}</td>
        <td>₹{row.totalAmount.toFixed(2)}</td>
        <td>{row.commissionPercentage.toFixed(2)}%</td>
        <td>₹{row.totalIncome.toFixed(2)}</td>
        <td>₹{row.gstAmount.toFixed(2)}</td>
        <td>₹{row.customerAmount.toFixed(2)}</td>
        <td>₹{row.customerWelfareAmount.toFixed(2)}</td>
        <td>₹{row.headOfficeAmount.toFixed(2)}</td>
        <td>₹{row.stateAdminAmount.toFixed(2)}</td>
        <td>₹{row.msbAmount.toFixed(2)}</td>
        <td>{paymentModeLabel(row)}</td>{/* [CASH-MODE] */}
      </tr>
    ));
  };

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      {/* Place BackButton outside the Grid */}
      <BackButton />
      <br />  <br />
      
      {/* Loading Backdrop */}
      <Backdrop 
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2
        }} 
        open={loading}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6" align="center">
          Processing Commission Split Data...
        </Typography>
        <Typography variant="body2" align="center" sx={{ maxWidth: 400 }}>
          We're analyzing detailed commission breakdowns from our database. 
          This may take a few moments for large date ranges.
        </Typography>
      </Backdrop>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <div style={{ padding: '20px' }}>
          <Typography variant="h6" style={{ marginTop: '10px' }}>
            Total Shop Commission Split Report (Detailed)
          </Typography>
          
          {/* Info Alert */}
          <Alert severity="info" sx={{ marginTop: 2, marginBottom: 2 }}>
            This report shows individual commission split transactions with detailed breakdowns. Large date ranges may take longer to process.
          </Alert>

          <Grid
            container
            spacing={2}
            alignItems="center"
            style={{ marginTop: '20px' }}
          >
            <Grid item xs={12} sm={4}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                renderInput={(params) => <TextField fullWidth {...params} />}
                maxDate={new Date()}
                inputFormat="dd-MM-yyyy"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                renderInput={(params) => <TextField fullWidth {...params} />}
                maxDate={new Date()}
                inputFormat="dd-MM-yyyy"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                color="primary"
                onClick={fetchSplitupData}
                style={{ marginRight: '10px' }}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Submit'}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={handlePrint}
                disabled={transactions.length === 0 || loading}
              >
                Print
              </Button>
            </Grid>
            {/* [CASH-MODE] Payment Mode filter */}
            <Grid item xs={12} sm={4}>
              <PaymentModeFilter value={paymentMode} onChange={setPaymentMode} fullWidth />
            </Grid>
          </Grid>

          {/* Summary Cards */}
          {transactions.length > 0 && (
            <Grid container spacing={2} style={{ marginTop: '20px' }}>
              <Grid item xs={12} sm={2}>
                <Paper elevation={3} style={{ padding: '10px', textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="primary">
                    Total Transactions
                  </Typography>
                  <Typography variant="h5">
                    {totalTransactions}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Paper elevation={3} style={{ padding: '10px', textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="primary">
                    Total Amount
                  </Typography>
                  <Typography variant="h6">
                    ₹{grandTotals.totalAmount.toFixed(2)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Paper elevation={3} style={{ padding: '10px', textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="primary">
                    Avg Commission %
                  </Typography>
                  <Typography variant="h6">
                    {grandTotals.averageCommissionPercentage.toFixed(2)}%
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Paper elevation={3} style={{ padding: '10px', textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="primary">
                    Total Income
                  </Typography>
                  <Typography variant="h6">
                    ₹{grandTotals.totalIncome.toFixed(2)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Paper elevation={3} style={{ padding: '10px', textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="primary">
                    Customer Amount
                  </Typography>
                  <Typography variant="h6">
                    ₹{grandTotals.customerAmount.toFixed(2)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Paper elevation={3} style={{ padding: '10px', textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="primary">
                    Head Office
                  </Typography>
                  <Typography variant="h6">
                    ₹{grandTotals.headOfficeAmount.toFixed(2)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Additional Summary Cards Row */}
          {transactions.length > 0 && (
            <Grid container spacing={2} style={{ marginTop: '10px' }}>
              <Grid item xs={12} sm={2}>
                <Paper elevation={3} style={{ padding: '10px', textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="primary">
                    GST Amount
                  </Typography>
                  <Typography variant="h6">
                    ₹{grandTotals.gstAmount.toFixed(2)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Paper elevation={3} style={{ padding: '10px', textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="primary">
                    Welfare Amount
                  </Typography>
                  <Typography variant="h6">
                    ₹{grandTotals.customerWelfareAmount.toFixed(2)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Paper elevation={3} style={{ padding: '10px', textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="primary">
                    State Admin
                  </Typography>
                  <Typography variant="h6">
                    ₹{grandTotals.stateAdminAmount.toFixed(2)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Paper elevation={3} style={{ padding: '10px', textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="primary">
                    MSB Amount
                  </Typography>
                  <Typography variant="h6">
                    ₹{grandTotals.msbAmount.toFixed(2)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* [CASH-MODE] UPI vs Cash */}
          {transactions.length > 0 && (
            <PaymentModeTotals totals={splitModeTotals} amountKey="totalAmount" title="Total Amount" sx={{ mt: 2 }} />
          )}

          {!loading && (
            <div style={{ marginTop: '20px', height: 600, width: '100%' }}>
              {transactions.length > 0 ? (
                <DataGrid
                  rows={rows}
                  columns={columns}
                  pageSize={25}
                  rowsPerPageOptions={[25, 50, 100]}
                  disableSelectionOnClick
                  loading={loading}
                  pagination
                  paginationMode="client"
                />
              ) : (
                <Typography variant="h6" align="center">
                  No commission split data found for the selected dates.
                </Typography>
              )}
            </div>
          )}

          <Dialog open={openDialog} onClose={handleDialogClose}>
            <DialogTitle>Message</DialogTitle>
            <DialogContent>
              <Typography>{dialogMessage}</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDialogClose} color="primary">
                OK
              </Button>
            </DialogActions>
          </Dialog>

          {/* Printable Component */}
          <div style={{ position: 'absolute', left: '-10000px', top: 0 }}>
            <div ref={componentRef} style={{ width: '1400px', padding: '20px' }}>
              <Typography variant="h4" align="center">
                Q-Pay Reports
              </Typography>
              <Typography
                variant="h6"
                align="center"
                style={{ marginTop: '10px' }}
              >
                Total Shop Commission Split Report (Detailed)
              </Typography>
              <Typography
                variant="subtitle1"
                align="center"
                style={{ marginBottom: '20px' }}
              >
                {`From ${format(startDate, 'dd-MM-yyyy')} to ${format(
                  endDate,
                  'dd-MM-yyyy'
                )}`}
              </Typography>
              
              {/* Summary Section for Print */}
              <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                <Typography variant="h6">Summary</Typography>
                <Typography>Total Transactions: {totalTransactions} | Total Amount: ₹{grandTotals.totalAmount.toFixed(2)} | Avg Commission: {grandTotals.averageCommissionPercentage.toFixed(2)}%</Typography>
                <Typography>Total Income: ₹{grandTotals.totalIncome.toFixed(2)} | Customer: ₹{grandTotals.customerAmount.toFixed(2)} | Head Office: ₹{grandTotals.headOfficeAmount.toFixed(2)}</Typography>
              </div>

              {transactions.length > 0 && (
                <table className="print-table" border="1" cellPadding="5">
                  <thead>
                    <tr>
                      <th>S.No.</th>
                      <th>Date</th>
                      <th>VPA ID</th>
                      <th>Total Amount (₹)</th>
                      <th>Commission %</th>
                      <th>Total Income (₹)</th>
                      <th>GST (₹)</th>
                      <th>Customer (₹)</th>
                      <th>Welfare (₹)</th>
                      <th>Head Office (₹)</th>
                      <th>State Admin (₹)</th>
                      <th>MSB (₹)</th>
                      <th>Mode</th>{/* [CASH-MODE] */}
                    </tr>
                  </thead>
                  <tbody>
                    {renderTableRows()}
                  </tbody>
                </table>
              )}
              
              <div style={{ marginTop: '20px' }}>
                <Typography variant="h6">Grand Totals:</Typography>
                <Typography>Total Amount: ₹{grandTotals.totalAmount.toFixed(2)} | Total Income: ₹{grandTotals.totalIncome.toFixed(2)}</Typography>
                <Typography>GST: ₹{grandTotals.gstAmount.toFixed(2)} | Customer: ₹{grandTotals.customerAmount.toFixed(2)} | Welfare: ₹{grandTotals.customerWelfareAmount.toFixed(2)}</Typography>
                <Typography>Head Office: ₹{grandTotals.headOfficeAmount.toFixed(2)} | State Admin: ₹{grandTotals.stateAdminAmount.toFixed(2)} | MSB: ₹{grandTotals.msbAmount.toFixed(2)}</Typography>
                {/* [CASH-MODE] */}
                <Typography>UPI: ₹{(Number(splitModeTotals.UPI && splitModeTotals.UPI.totalAmount) || 0).toFixed(2)} | Cash: ₹{(Number(splitModeTotals.CASH && splitModeTotals.CASH.totalAmount) || 0).toFixed(2)}</Typography>
              </div>
            </div>
          </div>
        </div>
      </LocalizationProvider>
    </Box>
  );
};

export default TotalShopCommissionSplitReport;